/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import * as Msdyn365 from '@msdyn365-commerce/core';
import { SalesOrder, SimpleProduct } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { Button, getPayloadObject, getTelemetryAttributes, getTelemetryObject,
    IModuleProps, INodeProps, ITelemetryContent, TelemetryConstant } from '@msdyn365-commerce-modules/utilities';
import classname from 'classnames';
import get from 'lodash/get';
import { computed } from 'mobx';
import * as React from 'react';

import { IGetOrderSummaryInput, IHelp, IOrderSummary, IPaymentMethods, IPaymentMethodsInput } from '../../common';
import { getGroups, IGroups } from '../../common/get-groups';
import { getOrderInformation, getOrderInformationInput, IOrderInformation } from '../../common/get-order-information';
import { ISalesLine } from '../../common/get-sales-line';
import { getSalesLines, getSalesLinesInput } from '../../common/get-sales-lines';
import { Label } from '../../common/label';
import { getTenderIdOperationIdMap, IPriceContext } from '../../common/utilities';
import { getOrderConfirmationHelp } from '../order-confirmation/components/get-help';
import { getOrderConfirmationOrderSummary } from '../order-confirmation/components/get-order-summary';
import { getOrderConfirmationPaymentMethods } from '../order-confirmation/components/get-payment-methods';
import { ICheckoutOrderConfirmationData } from './checkout-order-confirmation.data';
import { ICheckoutOrderConfirmationProps } from './checkout-order-confirmation.props.autogenerated';

export interface ICheckoutOrderConfirmationViewProps extends ICheckoutOrderConfirmationProps<ICheckoutOrderConfirmationData> {
    moduleProps: IModuleProps;
    salesOrder?: SalesOrder;
    footerProps?: INodeProps;
    alert?: React.ReactNode;
    loading?: React.ReactNode;
    heading: React.ReactNode;
    backToShoppingLink?: React.ReactNode;
    orderInfomation?: IOrderInformation;
    salesLines?: ISalesLine[];
    groups?: IGroups;
    orderSummary?: IOrderSummary;
    payment?: IPaymentMethods;
    help?: IHelp;
}

/**
 *
 * OrderConfirmation component.
 * @extends {React.PureComponent<IOrderConfirmationProps<IOrderConfirmationData>>}
 */
class OrderConfirmation extends React.PureComponent<ICheckoutOrderConfirmationProps<ICheckoutOrderConfirmationData>> {
    private readonly moduleName: string = 'ms-order-confirmation';

    private readonly telemetryContent?: ITelemetryContent;

    private readonly backToShoppingAttributes?: Msdyn365.IDictionary<string>;

    constructor(props: ICheckoutOrderConfirmationProps<ICheckoutOrderConfirmationData>) {
        super(props);
        this.telemetryContent = getTelemetryObject(this.props.context.request.telemetryPageName!, this.props.friendlyName, this.props.telemetry);
        const payLoad = getPayloadObject('click', this.telemetryContent, TelemetryConstant.BackToShopping);
        this.backToShoppingAttributes = getTelemetryAttributes(this.telemetryContent, payLoad);
    }

    @computed get isCurrentChannel(): boolean {
        if (!this.order) {
            return false;
        }
        return this.order.ChannelId === get(this.props, 'context.request.apiSettings.channelId');
    }

    @computed get isDataReady(): boolean {
        return get(this.props, 'data.checkout.status') === 'SUCCESS';
    }

    @computed get hasError(): boolean {
        return get(this.props, 'data.checkout.status') === 'FAILED';
    }

    @computed get order(): SalesOrder | undefined {
        return get(this.props, 'data.checkout.result.salesOrder');
    }

    @computed get products(): SimpleProduct[] {
        return get(this.props, 'data.checkout.result.orderedProducts') || [];
    }

    @computed get priceContext(): IPriceContext {
        const {
            id,
            typeName,
            context,
            telemetry,
            resources: { freePriceText }
        } = this.props;
        return {
            id,
            typeName,
            context,
            telemetry,
            freePriceText
        };
    }

    @computed get earnedPoints(): number {
        const loyaltyCard = this.props.data.loyaltyCard.result;
        if (loyaltyCard && loyaltyCard.RewardPoints && this.order) {
            const pointPrograms = new Set(loyaltyCard.RewardPoints.map(point => point.RewardPointId));
            const rewardLines = this.order.LoyaltyRewardPointLines || [];
            let total = 0;
            rewardLines.forEach(line => {
                if ((line.RewardPointAmountQuantity || 0) > 0 && pointPrograms.has(line.RewardPointId)) {
                    total += line.RewardPointAmountQuantity!;
                }
            });
            return total;
        }
        return 0;
    }

    @computed get orderSummaryProps(): IGetOrderSummaryInput | undefined {
        if (!this.order) {
            return;
        }

        const {
            resources: { orderSummaryHeading, orderSummaryItemsTotalLabel, orderSummaryShippingFeeLabel, orderSummaryTaxLabel, orderSummaryGrandTotalLabel, pointsEarnedLabel }
        } = this.props;
        return {
            order: this.order,
            earnedPoints: this.earnedPoints,
            priceContext: this.priceContext,
            resource: {
                orderSummaryHeading,
                orderSummaryItemsTotalLabel,
                orderSummaryShippingFeeLabel,
                orderSummaryTaxLabel,
                pointsEarnedLabel,
                orderSummaryGrandTotalLabel
            }
        };
    }

    @computed get paymentMethodsProps(): IPaymentMethodsInput | undefined {
        const tenderTypes = this.props.data.tenderTypes.result;
        if (!this.order || !this.order.TenderLines || !tenderTypes) {
            return;
        }

        const locale = get(this.props, 'context.request.locale') || 'en-US';

        const {
            resources: {
                paymentMethodsTitle,
                creditCardEndingLabel,
                giftCardEndingLabel,
                amountCoveredLabel,
                cashUsedLabel,
                loyaltyCardUsedLabel,
                customerAccountUsedLabel
            }
        } = this.props;

        return {
            priceContext: this.priceContext,
            tenderLines: this.order.TenderLines,
            tenderIdOperationIdMap: getTenderIdOperationIdMap(tenderTypes),
            locale,
            resource: {
                paymentMethodsTitle,
                creditCardEndingLabel,
                giftCardEndingLabel,
                amountCoveredLabel,
                cashUsedLabel,
                loyaltyCardUsedLabel,
                customerAccountUsedLabel
            }
        };
    }

    public render(): JSX.Element {
        const {
            resources: {
                noSalesOrderText,
                genericErrorMessage,
                needHelpLabel,
                helpLineNumberLabel,
                loadingLabel,
                backToShopping,
                orderItemLabel,
                orderItemsLabel,
                helpLineContactAriaLabel
            },
            config: { heading, contactNumber, showTimeslot }
        } = this.props;

        const moduleProps = { moduleProps: this.props, className: classname('ms-order-confirmation', this.props.config.className) };
        const backToShoppingUrl = Msdyn365.getUrlSync('home', this.props.context.actionContext) || '';
        const multiplePickupStoreSwitchName = 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature';
        const { channelDeliveryOptionConfig, featureState } = this.props?.data;
        const retailMultiplePickUpOptionEnabled = featureState?.result?.find(item => item.Name === multiplePickupStoreSwitchName)?.IsEnabled;

        let viewProps: ICheckoutOrderConfirmationViewProps = {
            ...this.props,
            moduleProps,
            heading: heading && heading.text && (
                <Msdyn365.Text
                    {...heading}
                    className='ms-order-confirmation__heading'
                    text={heading.text}
                    tag={heading.tag || 'h2'}
                    editProps={{ onEdit: this.handleCheckoutOrderConfirmationHeadingChange, requestContext: this.props.context.request }}
                />
            ),
            backToShoppingLink: backToShoppingUrl && (
                <Button className='ms-order-confirmation__btn-keep-shopping' href={backToShoppingUrl} {...this.backToShoppingAttributes}>
                    {backToShopping}
                </Button>
            ),
            footerProps: { className: 'ms-order-confirmation__footer' }
        };

        if (this.hasError) {
            viewProps = {
                ...viewProps,
                alert: <Label className='ms-order-confirmation__alert' label={genericErrorMessage} />
            };
            return this.props.renderView(viewProps) as React.ReactElement;
        }

        if (!this.isDataReady) {
            viewProps = {
                ...viewProps,
                loading: <Label className='ms-order-confirmation__loading' label={loadingLabel} />
            };
            return this.props.renderView(viewProps) as React.ReactElement;
        }

        if (!this.order) {
            viewProps = {
                ...viewProps,
                alert: <Label className='ms-order-confirmation__alert' label={noSalesOrderText} />
            };
            return this.props.renderView(viewProps) as React.ReactElement;
        }

        const salesLinesInput = getSalesLinesInput(this.props, this.moduleName, this.order, this.products, this.priceContext, false, showTimeslot, undefined, retailMultiplePickUpOptionEnabled, channelDeliveryOptionConfig?.result);
        const salesLines = salesLinesInput && getSalesLines(salesLinesInput);

        const orderInfomationInput = this.order && getOrderInformationInput(this.props, this.moduleName, this.order, this.priceContext);
        const orderInfomation = orderInfomationInput && getOrderInformation(orderInfomationInput);

        viewProps = {
            ...viewProps,
            salesOrder: this.order,
            orderInfomation,
            salesLines,
            groups: salesLines ? getGroups({
                moduleName: this.moduleName,
                salesLines,
                resources: { orderItemLabel, orderItemsLabel },
                showTimeslot,
                retailMultiplePickUpOptionEnabled
            }) : undefined,
            orderSummary: this.orderSummaryProps ? getOrderConfirmationOrderSummary(this.orderSummaryProps) : undefined,
            payment: this.paymentMethodsProps ? getOrderConfirmationPaymentMethods(this.paymentMethodsProps) : undefined,
            help: contactNumber ? getOrderConfirmationHelp({ needHelpLabel, helpLineNumberLabel, contactNumber, helpLineContactAriaLabel, telemetryContent: this.telemetryContent }) : undefined
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    public handleCheckoutOrderConfirmationHeadingChange = (event: Msdyn365.ContentEditableEvent) => this.props.config.heading!.text = event.target.value;

}

export default OrderConfirmation;
